import React, { useState, useCallback, useRef, useEffect } from 'react';
import Cropper from 'react-easy-crop';
import { ImagePlus, ZoomIn, ZoomOut, RotateCcw, ChevronLeft, Check } from 'lucide-react';
import {
  REEL_ASPECT,
  REEL_OUTPUT_WIDTH,
  REEL_OUTPUT_HEIGHT,
  getCroppedReelImage,
  blobToFile,
  isImageLargeEnough,
} from './feedCropUtils';
import './style/FeedImageEditor.css';

const MIN_ZOOM = 1;
const MAX_ZOOM = 4;

// Stages of the flow: 'select' -> 'crop' -> 'caption'
const STAGE = { SELECT: 'select', CROP: 'crop', CAPTION: 'caption' };

const FeedImageEditor = ({ setImgCaption, imgCaption, imageSrc, setImageSrc, handleSaveMedia }) => {
  const [stage, setStage] = useState(STAGE.SELECT);
  const [rawImage, setRawImage] = useState(null); // untouched, full-res source, used for re-cropping
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [sizeWarning, setSizeWarning] = useState(false);
  const [error, setError] = useState('');
  const [posting, setPosting] = useState(false);

  const fileInputRef = useRef(null);

  const onCropComplete = useCallback((_croppedArea, croppedPixels) => {
    setCroppedAreaPixels(croppedPixels);
  }, []);

  const handleFilePick = (e) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // allow re-selecting the same file later
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setError('Please choose an image file.');
      return;
    }
    if (file.size > 25 * 1024 * 1024) {
      setError('Image is too large (max 25MB).');
      return;
    }

    setError('');
    const reader = new FileReader();
    reader.onload = () => {
      const src = reader.result;
      // Check native resolution before committing to the crop stage
      const probe = new Image();
      probe.onload = () => {
        setSizeWarning(!isImageLargeEnough(probe.naturalWidth, probe.naturalHeight));
        setRawImage(src);
        setCrop({ x: 0, y: 0 });
        setZoom(1);
        setStage(STAGE.CROP);
      };
      probe.src = src;
    };
    reader.readAsDataURL(file);
  };

  const handleZoomSlider = (e) => setZoom(Number(e.target.value));

  const nudgeZoom = (delta) => {
    setZoom((z) => Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, +(z + delta).toFixed(2))));
  };

  const resetCrop = () => {
    setCrop({ x: 0, y: 0 });
    setZoom(1);
  };

  const confirmCrop = async () => {
    if (!croppedAreaPixels || !rawImage) return;
    try {
      const { dataUrl } = await getCroppedReelImage(
        rawImage,
        croppedAreaPixels,
        REEL_OUTPUT_WIDTH,
        REEL_OUTPUT_HEIGHT
      );
      setImageSrc(dataUrl);
      setStage(STAGE.CAPTION);
    } catch (err) {
      console.error('crop render error:', err);
      setError('Could not process that image. Try a different photo.');
    }
  };

  const backToCrop = () => {
    setStage(STAGE.CROP);
  };

  const changePhoto = () => {
    setRawImage(null);
    setImageSrc('');
    setStage(STAGE.SELECT);
  };

  const handlePost = async () => {
    setPosting(true);
    try {
      await handleSaveMedia();
    } finally {
      setPosting(false);
    }
  };

  // ── Stage: SELECT ──────────────────────────────────────────────────────
  if (stage === STAGE.SELECT) {
    return (
      <div className="rie-root">
        <button className="rie-dropzone" onClick={() => fileInputRef.current?.click()}>
          <ImagePlus size={40} strokeWidth={1.5} />
          <span className="rie-dropzone-title">Choose a photo</span>
          <span className="rie-dropzone-sub">Reel format · 9:16</span>
        </button>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleFilePick}
          className="rie-file-input"
        />
        {error && <p className="rie-error">{error}</p>}
      </div>
    );
  }

  // ── Stage: CROP ────────────────────────────────────────────────────────
  if (stage === STAGE.CROP) {
    return (
      <div className="rie-root">
        <div className="rie-crop-stage-header">
          <button className="rie-text-btn" onClick={changePhoto}>
            <ChevronLeft size={18} /> Change photo
          </button>
          <button className="rie-text-btn rie-reset-btn" onClick={resetCrop}>
            <RotateCcw size={15} /> Reset
          </button>
        </div>

        <div className="rie-crop-frame-wrap">
          <Cropper
            image={rawImage}
            crop={crop}
            zoom={zoom}
            aspect={REEL_ASPECT}
            minZoom={MIN_ZOOM}
            maxZoom={MAX_ZOOM}
            restrictPosition={true}
            showGrid={true}
            onCropChange={setCrop}
            onZoomChange={setZoom}
            onCropComplete={onCropComplete}
            classes={{
              containerClassName: 'rie-cropper-container',
              cropAreaClassName: 'rie-cropper-area',
            }}
          />
        </div>

        {sizeWarning && (
          <p className="rie-warning">This photo is a bit low-res — it may look soft once cropped.</p>
        )}

        <div className="rie-zoom-row">
          <button className="rie-icon-btn" onClick={() => nudgeZoom(-0.2)} aria-label="Zoom out">
            <ZoomOut size={18} />
          </button>
          <input
            type="range"
            className="rie-zoom-slider"
            min={MIN_ZOOM}
            max={MAX_ZOOM}
            step={0.01}
            value={zoom}
            onChange={handleZoomSlider}
            aria-label="Zoom"
          />
          <button className="rie-icon-btn" onClick={() => nudgeZoom(0.2)} aria-label="Zoom in">
            <ZoomIn size={18} />
          </button>
        </div>

        {error && <p className="rie-error">{error}</p>}

        <button className="rie-primary-btn" onClick={confirmCrop} disabled={!croppedAreaPixels}>
          Next
        </button>
      </div>
    );
  }

  // ── Stage: CAPTION ─────────────────────────────────────────────────────
  return (
    <div className="rie-root">
      <div className="rie-crop-stage-header">
        <button className="rie-text-btn" onClick={backToCrop}>
          <ChevronLeft size={18} /> Edit crop
        </button>
      </div>

      <div className="rie-preview-frame">
        <img src={imageSrc} alt="Your moment" className="rie-preview-img" />
      </div>

      <textarea
        className="rie-caption-input"
        value={imgCaption}
        onChange={(e) => setImgCaption(e.target.value)}
        placeholder="Add a caption…"
        rows={3}
      />

      <button className="rie-primary-btn" onClick={handlePost} disabled={posting}>
        {posting ? 'Posting…' : (
          <>
            <Check size={18} /> Post
          </>
        )}
      </button>
    </div>
  );
};

export default FeedImageEditor;